import { format, formatDistance } from 'date-fns';
import { id as localeId } from 'date-fns/locale';

// =============================================================================
// Format helper — DILARANG render raw value mentah ke user.
// Selalu pakai helper di sini agar konsisten antar halaman & locale Indonesia.
// =============================================================================

/**
 * formatKm: meter → kilometer string siap-display.
 * DB selalu simpan meter; UI selalu render km (lihat docs/glossary.md).
 *
 *   123     → "0.1 km"
 *   12345   → "12.3 km"
 *   123456  → "123 km"   (no decimal kalau >= 100km, biar tidak rame)
 */
export function formatKm(meters: number | null | undefined): string {
  if (meters == null || !isFinite(meters)) return '0 km';
  const km = meters / 1000;
  if (km >= 100) return `${km.toFixed(0)} km`;
  return `${km.toFixed(1)} km`;
}

/**
 * formatKmValue: kilometer → string siap-display.
 * Dipakai untuk field yang memang sudah disimpan dalam KM, misalnya
 * positions.daily_distance dan total_odo.
 */
export function formatKmValue(km: number | null | undefined): string {
  if (km == null || !isFinite(km)) return '0 km';
  if (km >= 100) return `${km.toFixed(0)} km`;
  return `${km.toFixed(1)} km`;
}

/**
 * formatSpeed: km/h → string siap display (sudah dalam km/h dari device, langsung pakai).
 *   0    → "0 km/h"
 *   45.6 → "46 km/h"   (round biar tidak rame)
 */
export function formatSpeed(kmh: number | null | undefined): string {
  if (kmh == null || !isFinite(kmh)) return '0 km/h';
  return `${Math.round(kmh)} km/h`;
}

/**
 * formatDateTime: parse "yyyy-MM-dd HH:mm:ss" (WIB) → "25 Mei 2026, 14:30".
 * Backend kirim string format ini di field tdate/sdate.
 */
export function formatDateTime(s: string | null | undefined): string {
  if (!s) return '-';
  // Backend kirim WIB wall-clock tanpa offset — parse sebagai local time.
  const d = new Date(s.replace(' ', 'T'));
  if (isNaN(d.getTime())) return s;
  return format(d, "d MMM yyyy, HH:mm", { locale: localeId });
}

/**
 * formatRelativeTime: "5 menit lalu", "2 jam lalu", dst. Untuk indikator
 * "last update". Output date-fns id dipangkas supaya ringkas:
 * "kurang dari 1 menit yang lalu" → "1 menit lalu",
 * "sekitar 7 jam yang lalu" → "7 jam lalu".
 */
export function formatRelativeTime(s: string | null | undefined): string {
  if (!s) return '-';
  const d = new Date(s.replace(' ', 'T'));
  if (isNaN(d.getTime())) return s;
  return formatDistance(d, new Date(), { addSuffix: true, locale: localeId })
    .replace(/\b(kurang dari|lebih dari|sekitar)\s+/g, '')
    .replace('yang lalu', 'lalu');
}

/**
 * formatDurationSeconds: 3725 → "1 jam 2 menit". Untuk total_move/idle/park.
 */
export function formatDurationSeconds(seconds: number | null | undefined): string {
  if (seconds == null || seconds <= 0) return '0 menit';
  const day = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const parts: string[] = [];
  if (day > 0) parts.push(`${day} hari`);
  if (h > 0) parts.push(`${h} jam`);
  if (m > 0 && day === 0) parts.push(`${m} menit`);
  return parts.length ? parts.join(' ') : '< 1 menit';
}
